import { ErrorHandler, inject, Injectable } from "@angular/core";
import { Logger } from "./logger.service";
import { isErrorLike, toErrorDetails } from "./log-value-sanitizer";

/**
 * Forwards every uncaught error to `Logger` so crashes are captured even when no code explicitly
 * calls it. `provideBrowserGlobalErrorListeners()` (already registered in `app.config.ts`)
 * routes `window.onerror`/`unhandledrejection` into Angular's `ErrorHandler`, so overriding it
 * here is enough — no raw `window` listeners are needed.
 *
 * Registered via `{ provide: ErrorHandler, useClass: AppErrorHandler }`.
 *
 * @see Docs/app-logs.md
 */
@Injectable()
export class AppErrorHandler extends ErrorHandler {
  private readonly logger = inject(Logger);

  override handleError(error: unknown): void {
    // Preserves Angular's default dev-console behavior before persisting the crash.
    super.handleError(error);

    const { name, message, stack, code } = toErrorDetails(error);
    // Anything that isn't an Error (e.g. a rejected `HttpErrorResponse`) is also kept whole as
    // `value`, since its `message` alone is usually just `"[object Object]"`.
    this.logger.error("unhandled", message, isErrorLike(error) ? { name, code } : { name, value: error }, stack);
  }
}
